/**
 * @file request.js
 * @description HTTP request utility module, built on top of Axios.
 * Provides unified request interception (Token injection),
 * response interception (global error notifications, login state validation),
 * and a RequireManager request management class with Loading state control and standardized response data parsing.
 * */
import { AXIOS_API } from '@/views/storege';
import { message } from 'ant-design-vue';
import router from "@/components/router";
import axios from 'axios';


// global variable for message throttling ...
let LAST_MESSAGE_TIME = 0;
const MESSAGE_THROTTLE_MS = 1000;


// create axios instance ...
const axios_service = axios.create({
    // baseURL: AXIOS_API
})


// add all interceptors ------------------------------------------------------------------------------------------------

// add all request interceptors ...
axios_service.interceptors.request.use(authorization_require_interceptor);

// add all response interceptors ...
axios_service.interceptors.response.use(general_response_interceptor);

// ---------------------------------------------------------------------------------------------------------------------


/**
 * General Response Interceptor
 * Handles the response data uniformly.
 * @param {Object} response - The response object.
 * @returns {Object} - The response object.
 * */
function general_response_interceptor(response) {

    // define success code list
    let proper_code = ['200', '201']
    let forced_logout_code = ['4444']

    // Do not intercept any non-JSON responses...
    if ( response.headers['content-type'] !== 'application/json' ) {
        return response
    }

    // If the response code is in the success list, return the response directly...
    if ( proper_code.includes(String(response.data.code)) ) { return response }

    // If the response code is in the forced logout list, show error message and redirect to login ...
    if ( forced_logout_code.includes(String(response.data.code)) ) {
        show_error_message(response.data.message);
        const { permission_manager } = require("@/utils/permissions");
        permission_manager.clear();
        router.push('/login');
        return response
    }

    // The other case we should show the error message ...
    show_error_message(response.data.error || response.data.message || 'Network Error');
    return response;
}


/**
 * Authorization Require Interceptor
 * Adds authentication token to the request headers.
 * @param {Object} config - The request configuration object.
 * @returns {Object} - The modified request configuration object.
 * */
function authorization_require_interceptor(config) {
    // 添加认证 token ...
    const token = sessionStorage.getItem('Authorization')
    if (token) { config.headers.Authorization = `${token}` }
    return config
}


/**
 * Show global error message with a throttling mechanism.
 * Prevents multiple error popups from appearing within a short time frame (defined by MESSAGE_THROTTLE_MS).
 * If the provided message is empty, null, or undefined, a default "Network Error" message will be displayed.
 *
 * @param {String|any} msg - The error message to be displayed in the notification box.
 * @returns {void}
 * */
function show_error_message(msg) {
    const now = Date.now();
    // if message throttling we should not do anything ...
    if (now - LAST_MESSAGE_TIME < MESSAGE_THROTTLE_MS) { return; }
    // if the error message did not contain any info, we should show default error message ...
    if (!msg || (typeof msg === 'string' && msg.trim().length === 0)) { msg = "Network Error"; }
    // show error message box ...
    message.error(msg);
    // set last message time is now ...
    LAST_MESSAGE_TIME = now;
}


/**
 * RequireManager class ...
 * A wrapper class for managing HTTP requests, implemented based on axios
 * Provides unified request configuration, interceptor handling, and error management mechanism
 * Supports loading state control and custom error prompts
 */
class RequireManager {

    /**
     * The RequireManager constructor method ...
     * @param {String} axios_api - axios api url
    */
    constructor(axios_api) {
        this.axios_api = axios_api;
    }

    /**
     * combine url ...
     * @param {String} app_url - app url
     * @returns {String}
     */
    combine_url(app_url) {
        return `${this.axios_api}/${app_url}`
    }

    /**
     * get request ...
     * @param {String} url - request url
     * @param {Object} params - request params
     * @param {Object} loading - optional loading object setup ...
     * @param {String} error_message - optional error message ...
     * @param {Boolean} count_data - calculation data total length if need ...
     * @returns {Promise<axios.AxiosResponse<any>>}
     */
    async get(url, params, loading = null, error_message = null, count_data = false) {
        let result = null

        // try to get the data ...
        try {
            if (loading) {loading.value = true} // make sure the loading start ...
            result = await axios_service.get(this.combine_url(url), { params: params })
        }
        // when error happened ...
        catch (error) {
            if (error_message) { show_error_message(error_message) }
        }
        // stop the loading status ...
        finally { if (loading) { loading.value = false } }

        // return the result ...
        if (!result && error_message) { show_error_message(error_message); return null }
        if (result && result.status !== 200) { show_error_message(`API ERROR : ${result.status}`) }
        if (!result) { return null }

        // analyze the result data ...
        return this.analyze_result(result.data, count_data)
    }

    /**
     * post request ...
     * @param {String} url - request url
     * @param {FormData} form_data - request params
     * @param {Object} loading - optional loading object setup ...
     * @param {String} error_message - optional error message ...
     * @param {Boolean} count_data - calculation data total length if need ...
     * @param config
     * @returns {Promise<axios.AxiosResponse<any>>}
     */
    async post(url, form_data,
               loading = null, error_message = null, count_data = false,
               config = null) {

        let result = null
        // try to get the data ...
        try {
            if (loading) {loading.value = true} // make sure the loading start ...
            result = (await axios_service.post( this.combine_url(url), form_data, config?config:undefined))
        }
        // when error happened ...
        catch (error) {
            if (error_message) { show_error_message(error_message) }
        }
        // stop the loading status ...
        finally { if (loading) { loading.value = false } }

        // return the result ...
        if (!result) {
            show_error_message(error_message); return null
        }
        if (result.status !== 200) {
            show_error_message(`API ERROR : ${result.status}`)
        }

        // analyze the result data ...
        return this.analyze_result(result.data, count_data)
    }

    /**
     * analyze result data and structure ...
     * @param {Object} result_data - request result object
     * @param {Boolean} count_data - calculation data total length if need ...
     * @returns result data
     */
    analyze_result(result_data, count_data = false) {
        // analyze the result data ...

        if (typeof result_data === 'string') {
            try {
                result_data = JSON.parse(result_data)
            }
            catch (e) {
                show_error_message("JSON PARSE FAILED:" + e.message)
            }
        }

        if (result_data.code === 200) {
            if (!result_data.data) { return true }
            if (!count_data) { return result_data.data }
            return { total: result_data.total, data: result_data.data }
        }
        else if ( result_data.code === 4444 )
        {
            show_error_message(result_data.message);
            router.push('/login')
            return null
        }
        else {
            show_error_message(result_data.message)
        }
    }
}


export const require_manager = new RequireManager(AXIOS_API)
export default axios_service
